import type { ConditionsConfig } from './conditions';
import type { RefereeConfig } from './refereeConfig';
import type { ActionKind } from '../types';

/** Fixed simulation tick length (§3). Not a tunable — the sim runs at exactly 30 Hz. */
export const DT = 1 / 30;

/** Default half length in ticks (§3, outcome plan §2 — 2 × 5 min). */
export const HALF_LENGTH_DEFAULT = 9000;

/** D41: playtest-tuned default halves — 2×90 s on the small (walled) pitches, 2×120 s on the big ones.
 *  Hosts can still override via SET_MODE.halfLength; HALF_LENGTH_DEFAULT stays as the plan's reference. */
export function defaultHalfLength(teamSize: number): number {
  return teamSize <= 5 ? 90 * 30 : 120 * 30;
}

/**
 * D144 — practice has no clock. A lesson, a drill and the local kickabout are rehearsal, not a fixture:
 * a half that expires mid-rep drags you into HALFTIME, swaps the sides under a drill that placed players
 * by side, and ends in a FULLTIME screen nobody asked for. `defaultHalfLength(5)` is 90 s, so a lesson
 * hit half-time in under two minutes.
 *
 * A half this long is never reached (≈ 2.3 years of ticks), which is the point: it is the *absence* of a
 * clock expressed in the field the sim already has, so no phase-machine special case and no new state.
 * LOCAL ONLY — the server clamps a lobby to `MIN/MAX_HALF_TICKS` and must keep doing so.
 */
export const ENDLESS_HALF_TICKS = 0x7fffffff;

/** Is this mode a no-clock practice mode? (The HUD hides the clock; no half ever ends.) */
export function isEndlessHalf(halfLengthTicks: number): boolean {
  return halfLengthTicks >= ENDLESS_HALF_TICKS;
}

/** Player movement + stamina constants (§5.2). */
export interface MovementConfig {
  readonly MAX_SPEED: number; // 8.0
  readonly SPRINT_MUL: number; // 1.35
  readonly SPRINT_CARRY_MUL: number; // D40 — sprint multiplier WITH the ball glued (a chasing defender closes)
  readonly CONTAIN_SPEED_MUL: number; // D40 — top speed while holding CONTAIN (no sprint, braced)
  readonly CONTAIN_ACCEL_MUL: number; // D40 — accel bonus while containing (quick lateral shuffles)
  readonly MAX_ACCEL: number; // 18 m/s²
  readonly TURN_RATE_MAX: number; // 12 rad/s (heading-based clamp)
  readonly TURN_EXEMPT_SPEED: number; // 0.5 — below this speed the turn clamp is exempt
  readonly FACE_SLEW: number; // 14 rad/s
  readonly WALK_SPEED: number; // 2.5 — regen threshold
  readonly SPRINT_DRAIN: number; // 0.06 /s
  readonly STAMINA_REGEN: number; // 0.035 /s while speed < WALK_SPEED
  readonly STAMINA_FACTOR_MIN: number; // 0.65 — staminaFactor = lerp(0.65, 1.0, stamina)
  readonly SPRINT_MIN: number; // 0.12 — sprint disengages below
  readonly SPRINT_REARM: number; // 0.20 — sprint may re-engage at/above (hysteresis)
  // per-action movement speed multiplier (§5.2/§5.4); absent ⇒ 1.0
  readonly ACTION_SPEED_MUL: Readonly<Partial<Record<ActionKind, number>>>;
}

/** Collision constants (§5.5). Mass convention: every body is unit-mass (§3). */
export interface CollisionConfig {
  readonly CAPSULE_R: number; // 0.35 — player/ref capsule radius
  readonly CAPSULE_H: number; // 1.8 — player/ref capsule height
  readonly E_P: number; // 0.1 — player-player restitution
  readonly SOLVER_ITERS: number; // 2 — de-penetration iterations over all pairs
  readonly BALL_PLAYER_REST: number; // 0.55 — ball bounce off a non-possessing player
  readonly THROUGH_LEGS_Z: number; // D52 — a crossing ball below this height may slip between the legs
  readonly THROUGH_LEGS_P: number; // D52 — probability of the nutmeg (seeded state.rng, ascending id)
  // action-added contact impulse (§5.5); absent action ⇒ 0
  readonly ACTION_IMPULSE: Readonly<Partial<Record<ActionKind, number>>>;
}

/** Ball physics constants (§5.3). Mass convention: ball is unit-mass (§3). */
export interface BallConfig {
  readonly GRAVITY: number; // 9.81 m/s²
  readonly KD_AIR: number; // 0.012 — air drag, applies airborne AND rolling
  readonly KM: number; // 0.0045 — Magnus coefficient
  readonly SPIN_DECAY: number; // 0.6 /s — exponential spin decay
  readonly MAX_SPIN: number; // 60 rad/s — |ω| clamp at kick time and in sim
  readonly BOUNCE_GRIP: number; // 0.15 — horizontal velocity loss on a bounce
  readonly GRIP_K: number; // 0.02 — spin↔velocity exchange on a bounce
  readonly REST_POST: number; // 0.78 — restitution off posts/crossbar
  readonly REST_SNAP_SPEED: number; // 0.05 — below this on the ground the ball is zeroed
  readonly RADIUS: number; // 0.11 — ball sphere radius
}

/**
 * The frozen per-match simulation config (§17). Delivered on the wire and embedded in the log;
 * predictor and replay build it only from that JSON, never bundle defaults. Grows one section per
 * milestone (movement, actions, referee, ai, switch, pitch, protocol, …).
 */
/** Action state-machine constants (§5.4) + the kick table (§5.3). */
export interface ActionsConfig {
  readonly DUR: Readonly<Partial<Record<ActionKind, number>>>; // fixed action durations, ticks
  readonly PUNCH_RECOVERY_WHIFF: number; // 15 (M5)
  readonly PUNCH_RECOVERY_HIT: number; // 8 (M5)
  readonly RAGDOLL_IMPULSE_MIN: number; // 10 — contact impulse ≥ this ⇒ RAGDOLL (§5.4d)
  readonly RAGDOLL_TACKLER: number; // 1 — a no-ball tackle also ragdolls the tackler (§5.4d)
  readonly PUNCH_VICTIM_IMPULSE: number; // 8 (M5)
  readonly PUNCH_REACH: number; // punch sphere radius in front of the puncher (§5.4)
  readonly kick: {
    readonly KICK_MIN: number; // 8
    readonly SHOOT_MIN: number; // 15 — tap-shot floor: a snap-shot is a real attempt (D34)
    readonly REACH: number; // 1.1
    readonly CHARGE_CAP: number; // 18 — 0.6 s to full power (D34: was 30; 1 s felt sluggish)
    readonly CHARGE_EXP: number; // 1.5 — chargeCurve(h) = (min(h,cap)/cap)^exp
    readonly AUTO_POWER: number; // D49 — 1: no hold-to-charge. PASS/THROUGH fire on press, power arrival-calibrated to the receiver; SHOOT always full. 0 = legacy charge curve. D137: the D88 set-piece bypass is gone — a taker charges like everyone else, i.e. not at all.
    readonly INTENT_TICKS: number; // D53 — a kick button HELD when possession arrives fires FIRST-TIME within this window of the claim (one-touch play; SHOOT skips its windup)
    // D137: OVERCHARGE_SCATTER is DELETED with the set-piece manual charge (§7.3 / PT4-P2 — "a shot is a
    // shot, holding is just aiming"). It was an unbounded direction randomizer: at the clip's 150-tick
    // hold it reached ±3.96 rad. CHARGE_CAP / CHARGE_EXP survive — they feed the live power curve, the
    // pass calibration and the keeper distributions.
    /** D137 (§7.3) — the self-lob rungs: [tap, double-tap]. Low speed, high elevation: it lifts the ball
     *  over heads while the carrier keeps running. The existing z-physics, aerial finish and knock-on
     *  machinery do the rest — a lobbed ball is a live loose ball. */
    readonly LOB: { readonly SPEED: readonly [number, number]; readonly ELEV: readonly [number, number] };
    readonly MAX: { readonly PASS: number; readonly SHOOT: number; readonly THROUGH: number };
    readonly LOFT: { readonly PASS: number; readonly SHOOT: number; readonly THROUGH: number }; // elevation rad
    readonly ELEV_GAIN: number; // 0.3 rad — max aimElev-driven extra loft (D18)
    /** D110 (HB-A) — the LOFT-modified pass table. IP:382 names a "loft table per action/charge" that
     *  §17 never tabulated; these are its numbers. Only read on the BTN.LOFT branches of applyKicks, so
     *  every un-lofted kick is byte-identical to pre-D110. */
    readonly HIGH: {
      readonly APEX: { readonly PASS: number; readonly THROUGH: number }; // m — solver apex floor; THROUGH hangs for runners
      readonly MAX_SPEED: number; // m/s launch cap — beyond max range the ball lands SHORT (saturate, §9.3 spirit)
      readonly MIN_ELEV: number; // rad — a "high" ball is never flatter than this
      readonly MAX_ELEV: number; // rad — …and never a mortar
    };
  };
  /** D113 (HB-D) — the aerial finish: volley + header bands and forces. Fills IP:406's HEADER_JUMP row. */
  readonly aerial: {
    readonly VOLLEY_Z: readonly [number, number]; // strike band: shin to chest
    readonly HEADER_Z: readonly [number, number]; // chest to jump-apex head
    readonly STRIKE_REACH: number; // horizontal reach of an aerial strike (= kick REACH)
    readonly HEADER_JUMP_VZ: number; // IP:406 verbatim — flight 14 t, apex tick 7 (DUR.HEADER_JUMP already 14)
    readonly HEADER_WINDOW: readonly [number, number]; // IP:406 "apex ticks 5–9" — the head-redirect window
    readonly HEAD_Z: number; // contact height over p.z
    readonly HEADER_SPEED: number; // m/s — a placed redirect, not a SHOOT
    readonly VOLLEY_SPEED: number; // m/s — between THROUGH 30 and SHOOT 34
    readonly VOLLEY_ELEV: number; // rad — = LOFT.SHOOT
    readonly REDIRECT_KEEP: number; // fraction of incoming ball speed folded into the strike
    readonly TAP_ARM: number; // D135 — 1: an armed SHOOT stays armed through the button's RELEASE while a free ball is airborne and finishable. A second press toggles it off. (A tap used to be client-synthesised into an 18-tick hold, so it worked only for short flights, invisibly.)
    readonly VOLLEY_WINDOW: number; // D135 — ticks of lookahead the VOLLEY gets. The header always had one; the volley's envelope was an instantaneous per-tick test, so ~2–3 eligible ticks against a real cross and outright tunnelling on fast or steep balls.
    readonly STRIKE_GOAL_AIM: number; // D135 (✅ user) — 1: an in-flight strike solves toward the goal frame, bounded by the contact-quality cone. A scuffed volley can still miss; the INTENT is always goalward.
    readonly QUALITY_MAX_DEV: number; // rad — worst-contact deviation (deterministic, no rng — HB-P10)
  };
  readonly slide: { readonly ENTRY_MUL: number; readonly DECEL: number }; // 1.4, 14 m/s² (§5.4)
  readonly duel: {
    readonly CLEAN_DOT: number; // 0.1 — D35: tackler on the ball's side (≥) ⇒ clean steal; else spill
    readonly SPILL_SPEED: number; // 6 — loose-ball squirt speed on a mediocre duel
    readonly SPILL_JITTER_RAD: number; // 0.5 — ± seeded spread on the spill direction
    readonly SPILL_COOLDOWN: number; // 4 — claim lockout ticks (a race, not an instant regrab)
    readonly POSSESSION_GRACE: number; // D89 — settle ticks after a CLEAN tackle win: the just-beaten carrier can't instantly re-take (tackle/trap/claim). Symmetric. < DRIBBLE_KNOCK_GRACE_TICKS so the winner's own knock never overwrites it.
    readonly WHIFF_STUMBLE: number; // 1 — whiffed standing tackle ⇒ GETUP stumble (input-ignored)
    readonly WHIFF_STUMBLE_SKIP: number; // D40 — fraction of GETUP skipped on a whiff (shorter punishment)
  };
  readonly trap: {
    // D45 Item 6: catch/trap (E). A loose ball in reach & low ⇒ clean catch below CONTROL_MAX, always
    // deflects above DEFLECT_MIN, seeded band in between. Vs a carrier: a weak clean-side poke only.
    readonly WINDOW: readonly [number, number]; // active ticks the trap can claim
    readonly REACH: number; // 1.0 — loose-ball claim radius
    readonly CONTROL_MAX: number; // 14 — ball speed ≤ this ⇒ clean catch (velocity zeroed)
    readonly DEFLECT_MIN: number; // 20 — ball speed ≥ this ⇒ always deflects
    readonly DEFLECT_DAMP: number; // 0.45 — deflect keeps this fraction of speed, away from the player
    readonly CARRIED_REACH_MUL: number; // 0.6 — vs a carried ball the poke reaches less far
  };
  readonly barge: {
    readonly REACH: number; // D40 — shoulder-to-shoulder contact distance (centre to centre)
    readonly PAR_DOT: number; // D40 — velocity-parallel cos ≥ this = running WITH the carrier (legal)
    readonly SIDE_DOT: number; // D40 — |cos(bearing vs carrier facing)| ≤ this = contact from the SIDE
    readonly KNOCK: number; // D40 — loose-ball squirt speed added on a landed barge
  };
  readonly block: {
    // D82 RMB/CONTAIN body block — a braced, wide obstacle. A defender holding CONTAIN (not carrying)
    // enters the BLOCK stance: slow, and his body walls opponent dribble touches + absorbs barges.
    readonly MOVE_MUL: number; // 0.55 — top-speed while braced (D132: was 0.35; BLOCK is a stance, not a root)
    readonly BRACE_MUL: number; // 0.4 — a barge/collision impulse INTO a blocker is scaled by this (he holds his ground; the attacker bounces)
  };
  readonly tackle: {
    readonly STAND_REACH: number; // 1.25 dispossess sphere (D17 RTT slack; D40 defense pass widened it)
    readonly LUNGE_BOOST: number; // D40 — instant velocity step toward the aim when a stand tackle starts
    readonly SLIDE_REACH: number; // 1.35 — the committal option reaches farther (D17)
    readonly STAND_WINDOW: readonly [number, number]; // ticks 2–7 (D17: was 2–5)
    readonly SLIDE_WINDOW: readonly [number, number]; // ticks 3–12 (D17: was 3–10)
    // D120: a clean tackle PLAYS the ball loose (the tackler is mid-lunge and cannot hold it) — these
    // are how hard, along carrier → tackler. The poke drops it at the tackler's feet so he steps onto
    // it when his action ends; the committal slide clears it out of the duel entirely.
    readonly POKE_SPEED: number; // clean TACKLE_STAND
    readonly CLEAR_SPEED: number; // clean TACKLE_SLIDE
  };
  readonly gk: {
    readonly CATCH_MAX: number; // 28 — ball speed ≥ this ⇒ parry not catch (§5.4)
    readonly CATCH_REACH: number; // catch capsule radius around the diving keeper (m)
    readonly STANDING_REACH: number; // D39 — auto-save reach for a STANDING keeper in his box (dives extend it)
    readonly STANDING_Q_MUL: number; // D39 — catch-quality multiplier for no-dive saves (parry-prone: rebounds stay in play)
    readonly CATCH_WINDOW: readonly [number, number]; // GK_DIVE ticks the catch capsule is live (§5.4)
    readonly DIVE_SPEED: number; // lateral dive velocity (m/s) toward the dive side
    readonly PARRY_E: number; // 0.35 — restitution of a parried shot (§5.4)
    readonly DISTRIBUTE_CHARGE: number; // charge ticks for a GK_HOLD distribution kick
    readonly PICKUP_REACH: number; // 1.2 — box pickup of a loose ball (D18)
    readonly SMOTHER_TRIGGER: number; // 2.0 — TACKLE-in-box becomes a smother when the carried ball is this close
    readonly SMOTHER_REACH: number; // 1.0 — smother wins if the carried ball is within this in the window
    readonly SMOTHER_REACH_SHIELDED: number; // 0.6 — vs a SHIELDing carrier
    readonly SMOTHER_WINDOW: readonly [number, number]; // ticks 2–6 (D18)
    readonly HOLD_SAFE_DIST: number; // D43 — the keeper distributes only once the nearest opponent is this far (or the hold limit forces it)
    readonly DIVE_VZ_MAX: number; // D125 — m/s: a dive LEAVES THE GROUND when the ball is crossing high (apex ≈ 0.52 m at 3.2; the bar is 2.0–2.44)
    readonly DIVE_HAND_Z: number; // D125 — m over p.z where the catch capsule rides during a dive
    readonly SETTLE_TICKS: number; // 8 — D35: entering GK_HOLD roots the keeper (gather the ball)
    readonly DIST_MIN_M: number; // D46 — a distribution target must be ≥ this far (8 m); a man at the keeper's boot is not a pass
    readonly DIST_MIN_UPFIELD: number; // D46 — the release aim's forward component must exceed this (0.2): the keeper physically can't release own-goal-ward
    readonly PUNT_ELEV: number; // D46 — lofted-punt elevation (0..1, 0.8) when no safe flat lane exists: a high ball clears a camped press
    readonly PUNT_BOUNCE_MARGIN: number; // D84 — keep a punt's first touchdown this far short of the far wall so the bounce can't rocket back to the keeper
    // D133 (§4) — the distribution SCORE. `qualifies` used to check lane + upfield + ≥8 m and never
    // whether the man was MARKED, and `pickTarget` was an absolute priority chain, so a covered caller
    // forced a doomed feed and a single qualifier was taken however tightly covered.
    readonly DIST_OPEN_MIN: number; // m — hard floor: no distribution to a man marked tighter than this
    readonly DIST_W_OPEN: number; // weight on how open the receiver is
    readonly DIST_W_CLEAR: number; // weight on the lane's narrowest gap
    readonly DIST_W_PROG: number; // weight on how far upfield it moves the ball
    readonly DIST_W_NEAR: number; // penalty on distance — a goal kick is a lay-off, not a hunt (D55)
    readonly DIST_HUMAN_BONUS: number; // a human is preferred… as a bonus, not an absolute (D42/D44)
    readonly DIST_CALL_BONUS: number; // …and a CALL is a strong preference, still not an override
    readonly DIST_AVOID_PENALTY: number; // D59's back-pass feeder is the LAST resort. Invariant: DIST_HUMAN_BONUS < this ≤ DIST_CALL_BONUS — it must beat the plain human preference and lose to an explicit call.
    readonly DIST_LEAD_CAP_SEC: number; // s — the flat release leads its man (assist:false has no D34 lead)
    readonly PUNT_SCAN_R: number; // m — radius the punt's flank score counts shirts in (was an inline 12)
    readonly PUNT_DEPTH_FRAC: number; // × pitch length — punt drop depth (was an inline 0.25)
    readonly PUNT_FLANK_FRAC: number; // × pitch width — punt flank offset (was an inline 0.35)
    readonly AUTO_DIVE: number; // 1 — the sim starts the D125 dive when standing reach will not do and nobody dove (PT4-P21)
    readonly HOLD_MIN_TICKS: number; // D46 — after this many GK_HOLD ticks the keeper distributes even under press (via a lofted punt); breaks the standoff deadlock
    readonly SKILL: number; // 1.0 — D35: multiplies catch quality (single difficulty knob)
    readonly Q_HOLD: number; // 0.62 — quality ≥ this ⇒ clean catch
    readonly Q_PARRY: number; // 0.32 — quality ≥ this ⇒ directional parry; below ⇒ beaten
    readonly Q_W_REACT: number; // 0.45 — weight of reaction time (flight ticks / REACT_FULL)
    readonly Q_W_DEMAND: number; // 0.30 — weight of reach demand (offset / CATCH_REACH)
    readonly Q_W_POWER: number; // 0.25 — weight of shot power (speed / SHOOT max)
    readonly REACT_FULL_TICKS: number; // 18 — flight time at/over which the keeper is fully set
    readonly PARRY_JITTER: number; // 0.06 — ± state.rng jitter applied ONLY in the parry band (D35)
    readonly PARRY_SPEED_MUL: number; // 0.4 — rebound speed factor on a parry
  };
}

/** Possession — soft magnetism (§5.6). */
export interface PossessionConfig {
  readonly CLAIM_MAX_SPEED: number; // 18 (D43 — passes are controllable at any charge)
  readonly CLAIM_RADIUS: number; // 0.9 (× CLAIM_RADIUS_MUL[cond])
  readonly CLAIM_Z: number; // 0.5
  /** D149 — the CEILING of the possession system's jurisdiction. A claim needs `z < CLAIM_Z`; a trap
   *  reaches to here. Above it no gathering verb exists, so a body is a plain obstacle again (§5.5) —
   *  D52's "a controllable ball belongs to the possession system" is only true where that is so. */
  readonly GATHER_Z: number; // 0.75
  readonly CARRY_POINT: number; // 0.45 — carry offset ahead of facing
  readonly SPRING_OMEGA: number; // 14 (× SPRING_MUL[cond]) — critically-damped carry spring
  readonly KNOCK_SPEED: number; // 11
  readonly KNOCK_FREE_TICKS: number; // 12
  readonly LOSE_DIST: number; // 1.6
  readonly DRIBBLE_KNOCK_SPEED_MIN: number; // D39 — carrier speed above which dribbling pushes the ball ahead
  readonly DRIBBLE_KNOCK_PERIOD: number; // D39 — ticks between dribble touches (staggered by entity id)
  readonly DRIBBLE_KNOCK_LEAD_MUL: number; // D39 — knocked ball speed = carrier speed × this (loose + claimable by ANYONE)
  readonly DRIBBLE_KNOCK_SPRINT_LEAD_MUL: number; // D40 — sprint-dribbling pushes the ball THIS much further ahead (and ignores close control)
  readonly DRIBBLE_KNOCK_GRACE_TICKS: number; // D47 — no knock-on this soon after a claim (the first touch settles the ball)
  readonly RECEIVE_GRACE_TICKS: number; // D131 — a fresh receiver is protected from EVERY take verb this long (claimCooldownOnly = null ⇒ claimBarred covers claim/tackle/trap), and from the body-block + barge, which read the same window. Ordered: < POSSESSION_GRACE < DRIBBLE_KNOCK_GRACE < DRIBBLE_KNOCK_PERIOD.
  readonly BODY_BLOCK_PAD: number; // D40 — extra contact pad: carried ball touching an opponent's body breaks the dribble
  readonly BODY_BLOCK_CLOSING_MIN: number; // D131 — m/s the CARRIER must be driving into the defender before his own body-block strips him (was inline 1.5, a §17 breach)
  readonly BODY_BLOCK_FRONT_DOT: number; // D131 — how head-on that drive must be (was inline 0.5)
  readonly BLOCK_REL_CLOSING_MIN: number; // D132 — m/s of RELATIVE closing that strips against a defender who is BLOCKing: a set, braced body costs a dribbler who runs into it below the ordinary threshold. Carrier motion is still required (an advancing blocker cannot strip a standing man).
  readonly CONTAIN_BLOCK_MUL: number; // D40 — containing defenders present a wider blocking body
  readonly TOUCH_CUSHION: number; // D47 — residual incoming-ball velocity kept on a claim (first touch kills the pace)
  readonly RECEIVE_RADIUS_MUL: number; // D47 — claim-radius bonus when the ball is INCOMING (stepping into a pass)
  readonly TOUCH_DIRECTIONAL: number; // D53 — 1: a MOVING receiver's first touch pushes a fast ball into his stride (stays live a beat) instead of gluing it; 0 = D47 cushion-claim only
  readonly TOUCH_PUSH_SPEED: number; // D53 — stride-touch push (m/s added over the receiver's own velocity)
  readonly SHIELD_CONE_DOT: number; // D80 — back-cone shield: an opponent with dot(carrier.facing, carrier→opp) < −this can't tackle/claim the striding ball (the carrier's body screens it). Symmetric — bots + humans.
  readonly DRIBBLE_KNOCK_PUSH: number; // D81 — sprint knock is RELATIVE: knocked ball vel = carrier.vel + moveDir × this (a pace-proportional lead that always oscillates FORWARD), replacing the old speed × LEAD_MUL
  readonly DRIBBLE_KNOCK_ALIGN_MIN: number; // D81 — only knock while running roughly STRAIGHT (dot(velDir, inputDir) ≥ this); mid-turn the ball stays glued so a turn never knocks it sideways/behind
}

/** Match-flow / rules timings (§6). Durations in ticks (30 Hz). */
export interface RulesConfig {
  readonly KICKOFF_TICKS: number; // 90 — kickoff placement window (§6.1)
  readonly RESTART_MAX_TICKS: number; // 300 — restart auto-place cap (§6.1). D129: the comment said 90 for two waves; the value has been 300 (10 s) since D38 — see the default below.
  readonly TAKER_CLAIM_RADIUS: number; // D129 — m: the DESIGNATED taker gathers a dead ball from this far (everyone else keeps possession.CLAIM_RADIUS). The clip's taker stood 2.1 m off the spot pressing to no effect.
  readonly CEREMONY_TICKS: number; // D83 — restart soft-lock: the ball is UNTAKEABLE this many ticks after a set-piece (kick-off/free-kick/penalty) so players reposition. Added ON TOP of the take window, so the auto-timeout still counts from ceremony END.
  readonly CEREMONY_TICKS_QUICK: number; // D83 — a shorter ceremony for frequent low-stakes restarts (throw-in/kick-in/corner/goal-kick/drop-ball)
  readonly CEREMONY_TICKS_FREE_KICK: number; // D122 — a free kick keeps its context (same pitch, no reset), so it needs orientation time, not a cutscene. KICKOFF/PENALTY keep CEREMONY_TICKS.
  readonly PENALTY_TAKER_POCKET: number; // D122 — m behind the spot the taker may roam pre-whistle (he used to be re-placed on the spot EVERY tick, so a human literally could not move)
  readonly WALLED_MAX_SIZE: number; // D40 — teamSize ≤ this plays in a walled cage: NO out-of-bounds, ball rebounds
  readonly GOAL_CELEBRATION_TICKS: number; // 150 (§6.1)
  /** D108 — the goal-celebration ceremony: who mobs whom, and how fast. */
  readonly celebration: {
    readonly ORBIT_RADIUS: number; // m — the ring teammates run around the scorer on
    readonly ORBIT_SPEED: number; // m/s — tangential jog (a run, not a sprint)
    readonly ORBIT_PULL: number; // 1/s — how hard an off-ring player is pulled back onto it
    readonly MOPE_SPEED: number; // m/s — the conceding team's dejected walk
    readonly OWN_GOAL_CELEBRANTS: number; // how many celebrate when no scorer can be credited
  };
  readonly HALFTIME_TICKS: number; // 300 (§6.1)
  readonly MAX_OVERRUN: number; // 900 — hard cap past halfLength before a forced stop (§6.4)
  readonly OT_LENGTH_TICKS: number; // 5400 — golden-goal overtime length (§6.5)
  readonly TRAPPED_TICKS: number; // 150 — trapped free ball ⇒ DROP_BALL (§6.2)
  readonly TRAPPED_SPEED: number; // 0.1 — trapped-ball speed threshold (§6.2)
  readonly TRAPPED_MAX_Z: number; // 0.1 — a trapped ball must be grounded (below this height)
  readonly SHOT_MARGIN: number; // 2.0 — on-target aim margin beyond the goal frame (§6.2)
  readonly PENALTY_BOX_MARGIN: number; // 0.5 — how far behind the box edge non-taker players are placed (§6.2)
}

/** Team AI (§8). The AI is outside the sim — it reads state and emits PlayerInputs server-side, using its
 *  own seeded aiRng. Misconduct probabilities (P_*) are per-opportunity, latched on the rising edge (§8.1). */
export interface AiConfig {
  readonly PLAN_PERIOD: number; // 10 — re-plan a team every N ticks (§8.1)
  readonly SHOOT_T: number; // 0.09 — shoot when xG exceeds this (§8.1)
  readonly XG_SCALE: number; // 0.9 — xG = clamp01(XG_SCALE·exp(−dist/XG_DIST)·max(0,cosθ))
  readonly XG_DIST: number; // 12 — xG distance falloff (m)
  readonly BALL_SHIFT: number; // 0.35 — formation ball-shift fraction (§8.1; legacy — D57 SHAPE supersedes it in formationTargets)
  /** D57-B (agent2d-lite): SHAPE-PRESERVING ball-anchored formation block. The block SLIDES with the
   *  ball (per-role weights — never a lerp toward the ball point, which made blob football), pushes up
   *  in ATTACK, drops in DEFEND, and DEF/MID are guaranteed goal-side of the ball when defending.
   *  This is the "tune data, not code" table for team shape. */
  readonly SHAPE: {
    readonly LONG: { readonly DEF: number; readonly MID: number; readonly FWD: number }; // slide with ball x (fraction of normalized ball x)
    readonly LAT: { readonly DEF: number; readonly MID: number; readonly FWD: number }; // slide with ball y
    readonly ATTACK_PUSH: number; // extra upfield shift in ATTACK (normalized x)
    readonly DEFEND_DROP: number; // drop-off in DEFEND
    readonly GOALSIDE_GAP: { readonly DEF: number; readonly MID: number }; // defending: stay this far GOAL-side of the ball (normalized x)
  };
  /** D57-A: utility-carrier weights + feel constants (Mark/Dill GDC-2010 pattern). */
  readonly UTIL: {
    readonly SHOOT: number;
    readonly PASS: number;
    readonly THROUGH: number;
    readonly DRIBBLE: number;
    readonly CLEAR: number;
    readonly HUMAN_MUL: number; // D42 doctrine — a HUMAN pass target scores UP TO this much higher (D59: earned — scaled by his openness)
    readonly CALLER_MUL: number; // an explicit CALL nearly always wins
    readonly RETURN_MUL: number; // D59: penalty on passing straight back to the mate who just fed me (calling exempts)
    readonly RETURN_TICKS: number; // D59: how long after gaining the ball the return pass stays suppressed
    readonly INERTIA_MUL: number; // bonus on the standing choice (no decision ping-pong)
    readonly NOISE: number; // ± fraction of seeded per-option score wobble (never metronomic)
    readonly PERIOD: number; // re-decide cadence (ticks) — D115: × the profile's reactionMul
    readonly COMMIT_TICKS: number; // D115 — a CHANGED decision waits this long before it may fire (× reactionMul)
    readonly SERVE_MUL: number; // D115 — × SHOOT while a human is near (D42 serve doctrine; was a hard-coded 0.45)
    readonly HANDOFF_MUL: number; // D115 — × DRIBBLE while a human is near (was a hard-coded 0.35)
  };
  readonly PRESSURE_DIST: number; // 2.5 — a carrier is "pressured" within this (§8.1)
  readonly GK_HOLD_BAND: readonly [number, number]; // D125 — arc-hold hysteresis: start moving above [1], stop below [0]. Was a hard d > 0.4, which is a sprint-stop-sprint oscillator at the boundary.
  readonly GK_APPROACH_EXIT: number; // D125 — m/s: 'a shot is coming at me' RELEASES here (it still ENTERS at −2), so a ball hovering near the threshold cannot flip the keeper's whole behaviour every tick
  readonly TACKLE_EXPOSED_DIST: number; // 1.2 — stand-tackle when the ball is this exposed (§8.1)
  /** D124 — the free-kick wall. Replaces the literals that were inline in `freeKickWallSpot`. */
  readonly WALL: {
    readonly MAX_DIST_FRAC: number;
    readonly SIZES: readonly number[]; // ascending distance fractions; index → wall size (3, 2, 1)
    readonly MAX: Readonly<Record<number, number>>; // teamSize → max men in the wall
    readonly GAP: number;
    readonly POST_BIAS: number;
    readonly JOIN_RADIUS: number;
  };
  readonly SETTLE_TICKS: number; // D115 — a bot's FIRST TOUCH: ticks after a genuine reception before he may kick (× reactionMul)
  readonly SETTLE_JITTER: number; // D115 — ± ticks on the above, per (player, spell), so a team never releases on one beat
  readonly TACKLE_REACT_TICKS: number; // 12 — the ball must stay exposed this long before the stand tackle fires (D32)
  readonly TRAP_REACT_TICKS: number; // D136 — × reactionMul before a bot's TRAP fires. It was a frame-perfect reflex with no difficulty attach point at any level.
  readonly P_BARGE: number; // D136 — per-APPROACH roll before the auto-shove (latched, so one roll decides one approach, not one per tick)
  readonly WRONG_BALL_P: number; // D136 — baseline rate at which a carrier think takes the SECOND-best option. The legible "he picked the wrong pass" moment, scaled by the profile.
  readonly DUEL_MISS_P: number; // D136 — baseline rate at which an armed bot tackle mistimes and re-arms instead of pressing. THIS is D116's contest-roll item, resolved in the AI layer: the sim stays difficulty-free and a human's legal tackle still always wins (PT4-P6).
  readonly BASE_MOVE_CAP: number; // D136 — baseline cap on a bot's off-ball move-input magnitude: bots jog rather than max-sprint everywhere. L4/L5 chaseSpeedCap ≥ 1 compensates above it.
  readonly TACKLE_SLIDE_DIST: number; // 2.5 — slide when the carrier is within this and pulling away
  readonly CALL_OPEN_DIST: number; // 4.0 — CALL when this open (nearest opponent farther than this)
  readonly DRIBBLE_SPRINT_OPEN_DIST: number; // D132 — m of open space ahead before a bot CARRIER sprints. The D39 knock-on gates on the sprint latch, so a bot who never sprints never exposes the ball: this is the asymmetry behind "they just run past me".
  readonly CHASE_SPRINT_DIST: number; // D132 — a bot chaser beyond this from the ball sprints (and pays the stamina)
  readonly SHOOT_URGENT_KEEPER_DIST: number; // 4 — in the opponent box with the keeper this close ⇒ shoot NOW (D18)
  readonly PASS_ASSIST: number; // 1 = PASS/THROUGH soft-lock to the cone receiver's meeting point (D18, reworked D34)
  readonly PASS_ASSIST_MAX_RAD: number; // ~10° — legacy cap (superseded by soft-lock D34; kept for compat)
  readonly THROUGH_LEAD_SEC: number; // 0.8 — THROUGH aims this far ahead of the receiver beyond PASS (D34)
  readonly RECEIVER_CONE_RAD: number; // ~30° — receiver-inference cone (D18, switching+assist)
  readonly aggression: number; // 0.5 — difficulty block (Sunday league)
  readonly reactionDelayBase: number; // 4 — bot reaction delay ticks (±jitter)
  readonly reactionDelayJitter: number; // 2
  readonly aimNoiseSigma: number; // ~0.07 rad (4°) — D116: the ± aim error a BASELINE bot carries, × the profile's aimNoiseMul (declared in §17 since M1; nothing read it until D116)
  readonly passElevSigma: number; // D116 — the ± mis-hit a BASELINE bot puts on a PASS, in PlayerInput.aimElev units, × passErrorMul. The one pass channel the D34 soft-lock does not overwrite
  readonly decisionHysteresis: number; // 8 — ticks a decision sticks (§8.1)
  readonly GRUDGE_TICKS: number; // 300 (M5)
  readonly P_TACTICAL_FOUL: number; // 0.15 (M5)
  readonly P_RETALIATE: number; // 0.08 (M5)
  readonly P_DIVE_MUL: number; // 0.2 — P_DIVE = aggression·this (M5)
  readonly P_REARM: number; // 90 — opportunity re-arm window (§8.1)
  readonly REF_BLIND_MAX: number; // 0.5 — a bot only risks a punch when ref clarity on it is below this
  readonly TACTICAL_XG_MIN: number; // 0.15 — only foul a carrier whose xG exceeds this (§8.1)
  readonly GK_DEPTH: number; // 2.2 — keeper depth off the line, scaled (§8.1)
  readonly GK_REACT_TICKS: number; // 6 — keeper reaction before a dive (§8.1)
  readonly GK_SHUFFLE_SPEED: number; // D87 — cap on the keeper's LATERAL pre-dive shuffle (m/s); the DIVE covers the rest. At full MAX_SPEED the shuffle warped across the mouth in a few 30 Hz snapshots ("teleport parry")
  readonly gk1v1: {
    // D86 close-down: an opponent CARRYING the ball who is bearing down on goal ⇒ the keeper comes off his
    // line along the ball→goal-centre line (the shot-cone bisector), narrowing the angle, then smothers.
    readonly TRIGGER_DIST: number; // 11 — a carried ball this close to the goal (in front, roughly central) triggers the rush
    readonly WIDTH_MUL: number; // 1.6 — only rush a carrier within this × penaltyBoxHalfWidth of centre (don't chase a wide carrier out of position)
    readonly COMMIT_FRACTION: number; // 0.55 — advance to this fraction of the way from the goal toward the ball
    readonly COMMIT_MAX: number; // 6 — but never further than this off the line (chip/round protection)
  };
  readonly GK_HOLD_LIMIT: number; // 90 — GK_HOLD auto-distribute before this (§8.1)
  readonly GK_HOLD_STANDOFF: number; // 3.5 m — chasers hold off a keeper with the ball in hands (D36: pressing a held ball is futile)
  readonly CLEAR_PRESSURE_DIST: number; // D39 — own-box carrier with an opponent this close ⇒ first-time panic clearance
  readonly PASS_CONSIDER_DIST: number; // D39 — start looking for the pass at this pressure distance (well before PRESSURE_DIST desperation)
  readonly LANE_BLOCK_DIST: number; // D39 — an opponent within this of the passing lane disqualifies the target
  readonly CALL_BONUS_RAD: number; // D42 — pass soft-lock: a CALLING teammate wins vs a non-caller within this extra cone deviation
  readonly HUMAN_NEAR_DIST: number; // D42 — a human teammate within this of a bot carrier ⇒ the bot SUPPORTS (never attacks itself)
  readonly PASS_ARRIVE_SPEED: number; // D44 — target speed (m/s) at the receiver; passChargeFor charges so the pass ARRIVES with this pace
  readonly FEED_MAX_DELAY_TICKS: number; // D44 — feed release deadline (SUPERSEDED: D57-A's utility brain replaced the branch chain and its `feedBlockedSince` latch; nothing in src/ reads this. Retained for config-hash stability, like KNOCK_SPEED)
  readonly PASS_LEAD_HUMAN_SEC: number; // D44 — when passing to a human, aim ahead by target.vel × this (lands in stride, not at heels)
  /** D112 (HB-C) — the lane-aware long-pass ladder: how far ahead of the receiver THROUGH may lead, how
   *  many candidate lead points are scored, and how hard an overrun (past the receiver's reach) is docked. */
  readonly LONG: { readonly LEAD_MAX_SEC: number; readonly SAMPLES: number; readonly DEEP_W: number };
  /** D114 (HB-E) — when a carrier crosses: wide channel + final third, and the option's utility weight. */
  readonly CROSS: { readonly MIN_ATTACK_X: number; readonly WIDE_Y: number; readonly UTIL: number };
  readonly LOB_UTIL: number; // D114 — utility weight of the lofted through-ball (every flat lane shut)
  readonly HEADER_ARM_DIST: number; // D111 — a bot receiver holds SHOOT when the landing point is this close
  readonly DEF_HEADER: number; // D114 — 1: defenders contest crosses with a clearing header (0 = off, playtest lever)
}

/** Auto-switching (§8.2). Relevance weights + hysteresis for picking which entity a human session drives. */
export interface SwitchConfig {
  readonly EVAL_PERIOD: number; // 10 — re-evaluate switching every N ticks (§8.2). D134: a possession change or a session's own kick now forces an off-cadence pass, so this is the idle heartbeat, not the latency floor.
  readonly SWITCH_EASE_MS: number; // D134 — the camera pan on a switch (✅ the pan is liked; it is cosmetic and runs while you ALREADY have control). Client constant → config, value unchanged.
  readonly W_DIST: number; // 1.0
  readonly W_INTERCEPT: number; // 0.6
  readonly W_GOALSIDE: number; // 0.4 (defending only)
  readonly W_TAKER: number; // D123 — dead-ball bonus for restart.taker of YOUR team. Above GK_PENALTY on purpose: at a goal kick the taker IS the keeper, and without this Tab could never reach him.
  readonly RESTART_AUTO: number; // D123 — 1 = an AUTO session on the kicking team is routed to a BOT-held taker at award (never yanks another human); 0 = Tab only
  readonly GK_PENALTY: number; // 10 (drops to 0.5 when the ball is in the own box)
  readonly GK_PENALTY_BOX: number; // 0.5
  readonly H_MARGIN: number; // 0.25 — switch only if best − current exceeds this
  readonly H_COOLDOWN: number; // min ticks between auto-switches (post-switch lockout, anti flip-flop)
  readonly H_STICKINESS: number; // bonus added to the CURRENT holder's relevance — stops A↔B oscillation (§8.2)
  // D134: HANDOVER_TICKS is DELETED. It had been dead config since D123 — every ControlRouter apply
  // site passed `handoverTicks: 0` — and the whole handoverUntil / predictorHoldUntilTick machinery with
  // it. The SWITCH_ASSIGN wire byte stays, frozen at 0, so there is no protocol bump (PT4-P10 ✅).
  readonly GK_GOALWARD_MASK_TICKS: number; // D36 — after control lands on the keeper, own-goal-ward move input is masked this long (1 s)
  readonly CYCLE_CHAIN_TICKS: number; // D44 — Tab within this of the last Tab CYCLES the relevance ranking; a fresh press picks rank 0 (the best candidate)
  readonly roleBias: { readonly DEF: readonly [number, number, number]; readonly MID: readonly [number, number, number]; readonly FWD: readonly [number, number, number] }; // by ball third [def, mid, att]
  readonly AIR_FOLLOW: number; // D112 (HB-P6) — 1: a HIGH pass resolves its pass-follow at LAUNCH (you run onto the cross); ground passes keep touch-resolution
}

export interface SimConfig {
  readonly ball: BallConfig;
  readonly conditions: ConditionsConfig;
  readonly collision: CollisionConfig;
  readonly movement: MovementConfig;
  readonly bounds: BoundsConfig;
  readonly actions: ActionsConfig;
  readonly possession: PossessionConfig;
  readonly rules: RulesConfig;
  readonly ai: AiConfig;
  readonly switching: SwitchConfig;
  readonly referee: RefereeConfig;
}

export const DEFAULT_AI: AiConfig = Object.freeze({
  PLAN_PERIOD: 10,
  SHOOT_T: 0.09,
  XG_SCALE: 0.9,
  XG_DIST: 12,
  BALL_SHIFT: 0.35,
  SHAPE: Object.freeze({
    LONG: Object.freeze({ DEF: 0.45, MID: 0.55, FWD: 0.6 }),
    LAT: Object.freeze({ DEF: 0.3, MID: 0.45, FWD: 0.55 }),
    ATTACK_PUSH: 0.16,
    DEFEND_DROP: 0.14,
    GOALSIDE_GAP: Object.freeze({ DEF: 0.18, MID: 0.02 }),
  }),
  UTIL: Object.freeze({
    SHOOT: 1.0,
    PASS: 1.15, // football flows through passes — the brain leans distributive
    THROUGH: 0.75,
    DRIBBLE: 0.8,
    CLEAR: 0.7,
    HUMAN_MUL: 1.8,
    CALLER_MUL: 3.0,
    RETURN_MUL: 0.35,
    RETURN_TICKS: 45, // 1.5 s — enough for the passer to actually MOVE before he's a target again
    INERTIA_MUL: 1.18,
    NOISE: 0.2, // D136 (PT4-P12): 0.12 → 0.2, so the second-best ball genuinely wins sometimes
    PERIOD: 12, // D136: 9 → 12 (0.4 s). D115 took it 5 → 9; a bot still re-scored three times a second
    COMMIT_TICKS: 8, // D136: 6 → 8 — changing your mind is a movement, not a teleport
    SERVE_MUL: 0.75, // D115: was 0.45 — the D42 doctrine smothered the human's OWN team (see HUMAN_NEAR_DIST)
    HANDOFF_MUL: 0.7, // D115: was 0.35 — a mate who never runs at anyone is not support, he's a spectator
  }),
  PRESSURE_DIST: 2.5,
  GK_HOLD_BAND: [0.3, 0.7] as const,
  GK_APPROACH_EXIT: -0.5,
  TACKLE_EXPOSED_DIST: 1.2,
  WALL: Object.freeze({
    MAX_DIST_FRAC: 0.45, // wall only when the spot is this fraction of pitch length from our goal
    SIZES: [0.18, 0.3, 0.45] as const, // distance ladder (·length) → 3 / 2 / 1 men in the wall …
    MAX: Object.freeze({ 1: 0, 2: 1, 3: 2, 5: 3, 11: 5 }), // … capped per team size (3-a-side has two outfielders)
    GAP: 0.1, // m between shoulders — slot pitch is 2·CAPSULE_R + GAP
    POST_BIAS: 0.35, // fraction of wall width shifted to shadow the NEAR post
    JOIN_RADIUS: 1.2, // m — a defending human this close to the line anchors the wall and drags it
  }),
  SETTLE_TICKS: 13, // D136 (PT4-P12): 10 → 13 (~0.43 s). Still < DRIBBLE_KNOCK_PERIOD 14, which is the invariant that matters
  SETTLE_JITTER: 4, // ⇒ 6..14 ticks (0.2–0.47 s) at IDENTITY; derived, not drawn, so the AI rng order is untouched
  TACKLE_REACT_TICKS: 12, // 0.4 s at 30 Hz — a human-feeling reaction window; 15 collapses the press (D32 sweep)
  TRAP_REACT_TICKS: 4, // D136: ~0.13 s — a beat, not a reflex
  P_BARGE: 0.6,
  WRONG_BALL_P: 0.08,
  DUEL_MISS_P: 0.12,
  BASE_MOVE_CAP: 0.9,
  TACKLE_SLIDE_DIST: 2.5,
  CALL_OPEN_DIST: 4.0,
  DRIBBLE_SPRINT_OPEN_DIST: 5, // D132: m of open ray before a bot carrier puts his head down (⇒ D39 knock-on windows)
  CHASE_SPRINT_DIST: 8, // D132: a chaser this far off the ball sprints after it, stamina and all
  SHOOT_URGENT_KEEPER_DIST: 4,
  PASS_ASSIST: 1,
  PASS_ASSIST_MAX_RAD: 0.1745,
  THROUGH_LEAD_SEC: 0.8,
  RECEIVER_CONE_RAD: 0.5236,
  aggression: 0.5,
  reactionDelayBase: 4,
  reactionDelayJitter: 2,
  aimNoiseSigma: 0.1, // D136 (PT4-P12): 0.0698 → 0.10 (~5.7°). Was 4° — at 15 m that is ±1.05 m on a shot at the baseline; L5 tightens it to ±0.63 m, L1 opens it to ±3.1 m
  passElevSigma: 0.09, // D136 (PT4-P12): 0.06 → 0.09 — a baseline mis-hit now occasionally pops up off the deck. Original note follows.
  //  calibrated so a BASELINE mis-hit tops out at ~0.5 m/s of launch vz — the threshold at which planTeam calls a ball airborne. A level-3 pass stays a ground pass that skids; only the dumb half of the ladder scuffs one up into the air (which is what a scuffed pass IS)
  decisionHysteresis: 8,
  GRUDGE_TICKS: 300,
  P_TACTICAL_FOUL: 0.15,
  P_RETALIATE: 0.08,
  REF_BLIND_MAX: 0.5,
  TACTICAL_XG_MIN: 0.15,
  P_DIVE_MUL: 0.2,
  P_REARM: 90,
  GK_DEPTH: 2.2,
  GK_REACT_TICKS: 6,
  GK_SHUFFLE_SPEED: 6, // D87: ~human lateral shuffle (was full 8 m/s → the "teleport")
  gk1v1: Object.freeze({ TRIGGER_DIST: 11, WIDTH_MUL: 1.6, COMMIT_FRACTION: 0.55, COMMIT_MAX: 6 }),
  GK_HOLD_LIMIT: 90,
  GK_HOLD_STANDOFF: 8, // D46 Item 5: chasers respect a keeper with the ball in hands (real 6-second-rule distance); pressing a held ball is futile and used to force flat-into-the-camp releases
  CLEAR_PRESSURE_DIST: 4,
  PASS_CONSIDER_DIST: 5,
  LANE_BLOCK_DIST: 1.5,
  CALL_BONUS_RAD: 0.35,
  HUMAN_NEAR_DIST: 7, // D115: was 14 — a third of a 5-a-side pitch, so "a human is near" was almost always true

  PASS_ARRIVE_SPEED: 9, // D47: passes arrive with zip — receivable because the cushioned first touch kills the pace
  FEED_MAX_DELAY_TICKS: 20,
  PASS_LEAD_HUMAN_SEC: 0.25,
  LONG: Object.freeze({ LEAD_MAX_SEC: 1.4, SAMPLES: 5, DEEP_W: 0.3 }),
  CROSS: Object.freeze({ MIN_ATTACK_X: 0.65, WIDE_Y: 0.55, UTIL: 0.9 }),
  LOB_UTIL: 0.8,
  HEADER_ARM_DIST: 6,
  DEF_HEADER: 1,
});

export const DEFAULT_SWITCHING: SwitchConfig = Object.freeze({
  EVAL_PERIOD: 10,
  SWITCH_EASE_MS: 750,
  W_DIST: 1.0,
  W_INTERCEPT: 0.6,
  W_GOALSIDE: 0.4,
  W_TAKER: 12, // D123: > GK_PENALTY 10
  RESTART_AUTO: 1,
  GK_PENALTY: 10,
  GK_PENALTY_BOX: 0.5,
  H_MARGIN: 0.25,
  H_COOLDOWN: 6, // D49: 0.2 s — pass-follow hands over instantly now (no bot-driven window to guard), and a quick one-two must chain; the quiet period only kills same-eval flip-flop
  H_STICKINESS: 0.15, // the current holder must be beaten decisively before control leaves it (D22)
  GK_GOALWARD_MASK_TICKS: 30, // 1 s (D36 — keeper own-goal safety after a switch)
  CYCLE_CHAIN_TICKS: 45, // ≈ 1.5 s (D44 — a fresh Tab = best candidate; rapid re-press cycles the ranking)
  roleBias: Object.freeze({
    DEF: [0.3, 0.0, 0.0] as const, // +0.3 in the defensive third
    MID: [0.1, 0.2, 0.1] as const, // +0.2 mid, +0.1 elsewhere
    FWD: [0.0, 0.0, 0.3] as const, // +0.3 in the attacking third
  }),
  AIR_FOLLOW: 1, // D112/HB-P6
});

export const DEFAULT_RULES: RulesConfig = Object.freeze({
  KICKOFF_TICKS: 90,
  RESTART_MAX_TICKS: 300, // 10 s — D38: takers are teleported so bots restart in ~1 s; the long window is human aiming time (timeout → live scramble stays as the anti-freeze backstop)
  TAKER_CLAIM_RADIUS: 2.5, // D129: m — covers the observed 2.1 m fumble; > CLAIM_RADIUS 0.9 and > LOSE_DIST 1.6 so a taker who steps off the ball can always gather it back
  CEREMONY_TICKS: 90, // D83: ~3 s soft-lock on set-pieces (kick-off / free-kick / penalty)
  CEREMONY_TICKS_QUICK: 45, // D83: ~1.5 s on frequent restarts (throw-in / corner / goal-kick / drop-ball)
  CEREMONY_TICKS_FREE_KICK: 45, // D122: 1.5 s — the countdown ring + whistle carry the orientation load now
  PENALTY_TAKER_POCKET: 2.5, // D122: m — run-up room behind the spot
  WALLED_MAX_SIZE: 5,
  GOAL_CELEBRATION_TICKS: 150,
  celebration: {
    ORBIT_RADIUS: 2.4, // close enough to read as mobbing him, wide enough not to clip the rig
    ORBIT_SPEED: 3.4, // matches RUN_REFERENCE_SPEED, so the run clip plays at its authored cadence
    ORBIT_PULL: 2.0,
    MOPE_SPEED: 1.1, // below RUN_ANIM_MIN_SPEED×2 — a trudge, clearly not a jog
    OWN_GOAL_CELEBRANTS: 3,
  },
  HALFTIME_TICKS: 300,
  MAX_OVERRUN: 900,
  OT_LENGTH_TICKS: 5400,
  TRAPPED_TICKS: 150,
  TRAPPED_SPEED: 0.1,
  TRAPPED_MAX_Z: 0.1,
  SHOT_MARGIN: 2.0,
  PENALTY_BOX_MARGIN: 0.5,
});

export const DEFAULT_POSSESSION: PossessionConfig = Object.freeze({
  CLAIM_MAX_SPEED: 22, // D47: MAX.PASS is 26 now — hard passes must stay trappable (screamers ≥22 still beat feet)
  CLAIM_RADIUS: 0.9,
  CLAIM_Z: 0.5,
  GATHER_Z: 0.75, // = CLAIM_Z × 1.5, the trap's reach (was an un-named literal in tackles.ts)
  CARRY_POINT: 0.45,
  SPRING_OMEGA: 14,
  KNOCK_SPEED: 11,
  KNOCK_FREE_TICKS: 12,
  DRIBBLE_KNOCK_SPEED_MIN: 6.5, // only genuine sprints push the ball out
  DRIBBLE_KNOCK_PERIOD: 14,
  DRIBBLE_KNOCK_LEAD_MUL: 1.45, // D43 (SUPERSEDED by D81's additive DRIBBLE_KNOCK_PUSH — retained for config-hash stability, like KNOCK_SPEED)
  DRIBBLE_KNOCK_SPRINT_LEAD_MUL: 1.45, // D50 (SUPERSEDED by D81 — see above)
  DRIBBLE_KNOCK_GRACE_TICKS: 12, // D47: a sprinting receiver keeps their first touch (< DRIBBLE_KNOCK_PERIOD, so open-field cadence is barely changed)
  RECEIVE_GRACE_TICKS: 8, // D131: ~0.27 s — the clip's steals landed 6–7 ticks after the reception
  BODY_BLOCK_PAD: 0.05,
  BODY_BLOCK_CLOSING_MIN: 2.5, // D131: was inline 1.5 — a jog into a camped marker stripped you
  BODY_BLOCK_FRONT_DOT: 0.65, // D131: was inline 0.5 (~60°) — ~49.5° now, so a shoulder brush is not a drive
  BLOCK_REL_CLOSING_MIN: 2.0, // D132: a BRACED body is harder to run past than an unbraced one
  CONTAIN_BLOCK_MUL: 1.3,
  TOUCH_CUSHION: 0.25,
  RECEIVE_RADIUS_MUL: 1.35,
  TOUCH_DIRECTIONAL: 1, // D53: flip to 0 to restore pure cushion-claims (designer: "easily disable-able")
  TOUCH_PUSH_SPEED: 2.5,
  LOSE_DIST: 1.6,
  SHIELD_CONE_DOT: 0.3, // D80: ~107° back arc is shielded — steals must come from the side/front (or a genuinely loose touch)
  DRIBBLE_KNOCK_PUSH: 2.8, // D81: additive forward push (m/s) over the carrier's velocity — the ball leads by ~this, always in front
  DRIBBLE_KNOCK_ALIGN_MIN: 0.9, // D81: no knock while turning (velDir·inputDir < this) — the ball stays glued through a jink
});

export const DEFAULT_ACTIONS: ActionsConfig = Object.freeze({
  DUR: Object.freeze({
    KICK_RELEASE: 6,
    TACKLE_STAND: 10,
    TACKLE_SLIDE: 18,
    SLIDE_RECOVERY: 24,
    SHOVE: 8,
    PUNCH_WINDUP: 24, // D67: the throw fires on the 1st PUNCH_ACTIVE tick, so the windup is timed to the anims_punch fist-contact frame (raw ~1.96 s ÷ PUNCH_TIME_SCALE 2.5 ≈ 0.78 s ≈ 24 t) — keep in sync with client render/animatedPlayers.ts
    PUNCH_ACTIVE: 3,
    DIVE_FALL: 20,
    PRONE: 20,
    GETUP: 12,
    RAGDOLL: 45,
    HEADER_JUMP: 14,
    GK_DIVE: 16,
    GK_SMOTHER: 8, // D18
    GK_HOLD: 90,
    TRAP: 8, // D45 Item 6
    CELEBRATE: 60,
  }),
  PUNCH_RECOVERY_WHIFF: 15,
  PUNCH_RECOVERY_HIT: 8,
  RAGDOLL_IMPULSE_MIN: 10,
  RAGDOLL_TACKLER: 1,
  PUNCH_VICTIM_IMPULSE: 8,
  PUNCH_REACH: 1.2,
  kick: Object.freeze({
    KICK_MIN: 10, // D47: even a tap pass is crisp
    SHOOT_MIN: 15,
    REACH: 1.1,
    CHARGE_CAP: 18,
    CHARGE_EXP: 1.5,
    AUTO_POWER: 1, // D49: designer-directed — charge is dead, intensity is computed
    INTENT_TICKS: 12, // D53: 0.4 s of one-touch grace after the claim
    LOB: Object.freeze({ SPEED: Object.freeze([6, 8]) as unknown as readonly [number, number], ELEV: Object.freeze([1.1, 1.35]) as unknown as readonly [number, number] }), // D137/PT4-P15
    MAX: Object.freeze({ PASS: 26, SHOOT: 34, THROUGH: 30 }),
    LOFT: Object.freeze({ PASS: 0, SHOOT: 0.12, THROUGH: 0.2 }), // provisional loft — see DEVIATIONS
    ELEV_GAIN: 0.6, // D45 Item 3: raised 0.3→0.6 so the goal precision-aim top corners are reachable from ~18–24 m (bots use aimElev 0 ⇒ no golden impact)
    HIGH: Object.freeze({
      APEX: Object.freeze({ PASS: 2.4, THROUGH: 3.4 }), // a chip clears bodies; a cross HANGS for the runner
      MAX_SPEED: 28,
      MIN_ELEV: 0.3,
      MAX_ELEV: 1.05,
    }),
  }),
  aerial: Object.freeze({
    TAP_ARM: 1,
    VOLLEY_WINDOW: 3,
    STRIKE_GOAL_AIM: 1,
    VOLLEY_Z: [0.25, 1.25] as const,
    HEADER_Z: [1.25, 2.45] as const,
    STRIKE_REACH: 1.1, // = kick REACH
    HEADER_JUMP_VZ: 2.29, // IP:406
    HEADER_WINDOW: [5, 9] as const, // IP:406
    HEAD_Z: 1.7,
    HEADER_SPEED: 24,
    VOLLEY_SPEED: 30,
    VOLLEY_ELEV: 0.12,
    REDIRECT_KEEP: 0.25,
    QUALITY_MAX_DEV: 0.35,
  }),
  slide: Object.freeze({ ENTRY_MUL: 1.4, DECEL: 14 }),
  duel: Object.freeze({
    CLEAN_DOT: 0.1,
    SPILL_SPEED: 5, // D40: spills stay closer to the duel — the tackler more often wins his own spill
    SPILL_JITTER_RAD: 0.35, // D40: tighter toward the tackler's side
    SPILL_COOLDOWN: 4,
    POSSESSION_GRACE: 10, // D89: ~0.33 s at 30 Hz — outlasts a beaten defender's re-lunge windup, still < the 12-tick knock grace
    WHIFF_STUMBLE: 1,
    WHIFF_STUMBLE_SKIP: 0.3,
  }),
  trap: Object.freeze({ WINDOW: [1, 6] as const, REACH: 1.0, CONTROL_MAX: 14, DEFLECT_MIN: 20, DEFLECT_DAMP: 0.45, CARRIED_REACH_MUL: 0.9 }), // D82: was 0.6 — F was a near-useless poke; a real defensive tool now (still < STAND_REACH 1.25)
  barge: Object.freeze({ REACH: 1.0, PAR_DOT: 0.5, SIDE_DOT: 0.7, KNOCK: 3 }),
  block: Object.freeze({ MOVE_MUL: 0.55, BRACE_MUL: 0.4 }), // D82 body block; D132: 0.35 was a root nobody discovered
  tackle: Object.freeze({
    STAND_REACH: 1.25,
    LUNGE_BOOST: 2.5,
    SLIDE_REACH: 1.35,
    STAND_WINDOW: [2, 9] as [number, number],
    SLIDE_WINDOW: [3, 12] as [number, number],
    POKE_SPEED: 3, // D120: ~0.6 m of roll — inside CLAIM_RADIUS when TACKLE_STAND retires
    CLEAR_SPEED: 11, // D120: a slide wins territory, not possession — this leaves the duel
  }),
  gk: Object.freeze({
    CATCH_MAX: 28,
    CATCH_REACH: 1.9,
    STANDING_REACH: 1.1,
    STANDING_Q_MUL: 0.85,
    CATCH_WINDOW: [2, 13] as [number, number],
    DIVE_SPEED: 7,
    PARRY_E: 0.35,
    DISTRIBUTE_CHARGE: 14,
    PICKUP_REACH: 1.2,
    SMOTHER_TRIGGER: 2.0,
    SMOTHER_REACH: 1.0,
    SMOTHER_REACH_SHIELDED: 0.6,
    SMOTHER_WINDOW: [2, 6] as [number, number],
    HOLD_SAFE_DIST: 10,
    DIVE_VZ_MAX: 3.2,
    DIVE_HAND_Z: 1.2,
    SETTLE_TICKS: 8,
    DIST_MIN_M: 8,
    DIST_MIN_UPFIELD: 0.2,
    PUNT_ELEV: 0.8,
    PUNT_BOUNCE_MARGIN: 6, // D84: touchdown stays ≥ 6 m off the far wall
    DIST_OPEN_MIN: 3, // D133: a man marked inside 3 m is not an option, however convenient
    DIST_W_OPEN: 1.0,
    DIST_W_CLEAR: 0.6,
    DIST_W_PROG: 0.5,
    DIST_W_NEAR: 0.6,
    DIST_HUMAN_BONUS: 0.35,
    DIST_CALL_BONUS: 0.7, // a viable caller wins; a marked one no longer forces the pass
    DIST_AVOID_PENALTY: 0.7,
    DIST_LEAD_CAP_SEC: 0.5,
    PUNT_SCAN_R: 12,
    PUNT_DEPTH_FRAC: 0.25,
    PUNT_FLANK_FRAC: 0.35,
    AUTO_DIVE: 1,
    HOLD_MIN_TICKS: 30,
    SKILL: 1.0,
    Q_HOLD: 0.62,
    Q_PARRY: 0.32,
    Q_W_REACT: 0.45,
    Q_W_DEMAND: 0.3,
    Q_W_POWER: 0.25,
    REACT_FULL_TICKS: 18,
    PARRY_JITTER: 0.06,
    PARRY_SPEED_MUL: 0.4,
  }),
});

export const DEFAULT_MOVEMENT: MovementConfig = Object.freeze({
  MAX_SPEED: 8.0,
  SPRINT_MUL: 1.35,
  SPRINT_CARRY_MUL: 1.15, // D40: you still outpace a jogger, but a sprinting defender catches you
  CONTAIN_SPEED_MUL: 0.85,
  CONTAIN_ACCEL_MUL: 1.35,
  MAX_ACCEL: 18,
  TURN_RATE_MAX: 12,
  TURN_EXEMPT_SPEED: 0.5,
  FACE_SLEW: 14,
  WALK_SPEED: 2.5,
  SPRINT_DRAIN: 0.06,
  STAMINA_REGEN: 0.035,
  STAMINA_FACTOR_MIN: 0.65,
  SPRINT_MIN: 0.12,
  SPRINT_REARM: 0.2,
  ACTION_SPEED_MUL: Object.freeze({
    KICK_WINDUP: 0.55,
    SHIELD: 0.45,
    PUNCH_WINDUP: 0,
    PUNCH_ACTIVE: 0,
    PUNCH_RECOVERY: 0,
    GK_HOLD: 0.5,
    TRAP: 0.35, // D45 Item 6: a trap roots you (0.35× speed) while gathering
    HEADER_JUMP: 0, // D113: once you leave the ground you are committed — no steering in the air
  }),
});

/**
 * Arena containment (§dev). A soft/reflective wall `margin` metres outside the pitch that keeps the
 * ball and players in bounds. OFF by default (pure-sim tests run unbounded); the kickabout client
 * turns it on. Superseded by out-of-play → restarts when the rules slice (M3) lands.
 */
export interface BoundsConfig {
  readonly enabled: boolean;
  readonly margin: number; // metres outside the pitch rectangle
  readonly ballRestitution: number; // ball bounce off the wall
}

export const DEFAULT_BOUNDS: BoundsConfig = Object.freeze({ enabled: false, margin: 1.5, ballRestitution: 0.7 }); // D40: lively boards — wall-passes are a tool

export const DEFAULT_COLLISION: CollisionConfig = Object.freeze({
  CAPSULE_R: 0.35,
  CAPSULE_H: 1.8,
  E_P: 0.1,
  SOLVER_ITERS: 2,
  BALL_PLAYER_REST: 0.55,
  THROUGH_LEGS_Z: 0.25, // ankle-to-shin — rolling passes can nutmeg, anything at knee height and up always hits the body
  THROUGH_LEGS_P: 0.3,
  ACTION_IMPULSE: Object.freeze({ TACKLE_STAND: 4, TACKLE_SLIDE: 4, SHOVE: 6, PUNCH_ACTIVE: 10 }),
});

export const DEFAULT_BALL: BallConfig = Object.freeze({
  GRAVITY: 9.81,
  KD_AIR: 0.012,
  KM: 0.0045,
  SPIN_DECAY: 0.6,
  MAX_SPIN: 60,
  BOUNCE_GRIP: 0.15,
  GRIP_K: 0.02,
  REST_POST: 0.78,
  REST_SNAP_SPEED: 0.05,
  RADIUS: 0.11,
});
